/* eslint-disable no-console */
import type {
	ResolvedPos,
	Schema,
	Fragment,
	Slice,
	NodeType,
} from '@atlaskit/editor-prosemirror/model';
import { Slice as SliceClass } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

// ============================================================================
// Table Adjustment
// ============================================================================

export interface TableAdjustmentResult {
	adjusted: boolean;
	startPos: number;
	toPos: number;
}

/**
 * Adjusts streaming positions so we don't break table rows.
 */
export function calculateTableAdjustment({
	state,
	startPos,
	toPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	toPos: number;
	userContentEndPos: number;
}): TableAdjustmentResult {
	const doc = state.doc;
	const schema = state.schema;
	const docSize = doc.content.size;
	const bailout = { startPos, toPos, adjusted: false };

	if (startPos < 0 || startPos > docSize || toPos < 0 || toPos > docSize) {
		return bailout;
	}

	try {
		const $startPos = doc.resolve(startPos);
		const $toPos = doc.resolve(toPos);

		if (isWithinSameCell($startPos, $toPos, schema)) {
			return bailout;
		}

		const adjustedStartPos = getStartOfTableRow(schema, $startPos);
		const adjustedToPos = getEndOfTableRow(schema, $toPos);

		let newStartPos = startPos;
		let newToPos = toPos;
		let adjusted = false;

		if (adjustedStartPos !== undefined) {
			newStartPos = adjustedStartPos;
			adjusted = true;
		}

		if (adjustedToPos !== undefined) {
			newToPos = Math.min(adjustedToPos, userContentEndPos);
			adjusted = true;
		}

		return { startPos: newStartPos, toPos: newToPos, adjusted };
	} catch {
		return bailout;
	}
}

function isWithinSameCell($from: ResolvedPos, $to: ResolvedPos, schema: Schema): boolean {
	const tableCell = schema.nodes.tableCell;
	const tableHeader = schema.nodes.tableHeader;

	if (!tableCell && !tableHeader) {
		return false;
	}

	let fromCellDepth = -1;
	for (let d = $from.depth; d > 0; d--) {
		const node = $from.node(d);
		if ((tableCell && node.type === tableCell) || (tableHeader && node.type === tableHeader)) {
			fromCellDepth = d;
			break;
		}
	}

	if (fromCellDepth === -1) {
		return false;
	}

	let toCellDepth = -1;
	for (let d = $to.depth; d > 0; d--) {
		const node = $to.node(d);
		if ((tableCell && node.type === tableCell) || (tableHeader && node.type === tableHeader)) {
			toCellDepth = d;
			break;
		}
	}

	if (toCellDepth === -1) {
		return false;
	}

	return $from.start(fromCellDepth) === $to.start(toCellDepth);
}

function getStartOfTableRow(schema: Schema, resolvedPos: ResolvedPos): number | undefined {
	const tableRow = schema.nodes.tableRow;
	if (!tableRow) {
		return undefined;
	}

	for (let depth = resolvedPos.depth; depth > 0; depth--) {
		if (resolvedPos.node(depth).type === tableRow) {
			return resolvedPos.start(depth);
		}
	}
	return undefined;
}

function getEndOfTableRow(schema: Schema, resolvedPos: ResolvedPos): number | undefined {
	const tableRow = schema.nodes.tableRow;
	if (!tableRow) {
		return undefined;
	}

	for (let depth = resolvedPos.depth; depth > 0; depth--) {
		if (resolvedPos.node(depth).type === tableRow) {
			return resolvedPos.end(depth);
		}
	}
	return undefined;
}

// ============================================================================
// Sync Block Adjustment
// ============================================================================

export interface SyncBlockAdjustmentResult {
	adjusted: boolean;
	startPos: number;
	toPos: number;
}

/**
 * Adjusts toPos to account for bodiedSyncBlock nodes during intermediate streaming.
 *
 * - When the slice does NOT contain a bodiedSyncBlock, we cap toPos so that
 *   replaceRange does not accidentally remove the existing node.
 * - When the slice DOES contain a bodiedSyncBlock, we extend toPos past the
 *   existing node so that replaceRange replaces it rather than inserting a duplicate.
 *
 * A sync block is only "fully covered" if toPos extends past its END.
 * If toPos is in the MIDDLE of a sync block, we must extend past it.
 */
export function calculateSyncBlockAdjustment({
	state,
	fragment,
	startPos,
	toPos,
	userContentEndPos,
}: {
	fragment: Fragment;
	startPos: number;
	state: EditorState;
	toPos: number;
	userContentEndPos: number;
}): SyncBlockAdjustmentResult {
	const doc = state.doc;
	const docSize = doc.content.size;
	const bailout = { startPos, toPos, adjusted: false };

	if (startPos < 0 || startPos > docSize || toPos < 0 || toPos > docSize) {
		return bailout;
	}

	// Count sync blocks in the slice (the AI-generated fragment)
	let sliceSyncBlockCount = 0;
	fragment.forEach((node) => {
		if (node.type.name === 'bodiedSyncBlock') {
			sliceSyncBlockCount++;
		}
	});

	let newToPos = toPos;
	let adjusted = false;

	try {
		if (sliceSyncBlockCount === 0) {
			// Slice has NO sync blocks — cap toPos so replaceRange doesn't
			// accidentally delete any existing sync block.
			const $start = doc.resolve(startPos);

			// Case 1: startPos is inside a bodiedSyncBlock.
			// Cap toPos to the end of that block's inner content.
			let capped = false;
			for (let d = $start.depth; d > 0; d--) {
				if ($start.node(d).type.name === 'bodiedSyncBlock') {
					const endOfContent = $start.end(d);
					if (newToPos > endOfContent) {
						newToPos = endOfContent;
						adjusted = true;
					}
					capped = true;
					break;
				}
			}

			// Case 2: A bodiedSyncBlock starts ahead of startPos.
			// Cap toPos before it.
			if (!capped) {
				doc.nodesBetween(startPos, Math.min(newToPos, docSize), (node, pos) => {
					if (node.type.name === 'bodiedSyncBlock' && pos >= startPos && pos < newToPos) {
						newToPos = pos;
						adjusted = true;
					}
					return false;
				});
			}
		} else {
			// Slice HAS sync block(s) — extend toPos to cover the corresponding
			// sync blocks in the doc so replaceRange replaces them.
			//
			// A sync block is only "covered" if toPos extends past its END.
			let coveredSyncBlocks = 0;
			doc.nodesBetween(startPos, Math.min(newToPos, docSize), (node, pos) => {
				if (node.type.name === 'bodiedSyncBlock') {
					const end = pos + node.nodeSize;
					if (end <= newToPos) {
						coveredSyncBlocks++;
					}
				}
				return false;
			});

			const needToExtendPast = sliceSyncBlockCount - coveredSyncBlocks;

			if (needToExtendPast > 0) {
				let remaining = needToExtendPast;
				doc.nodesBetween(newToPos, Math.min(userContentEndPos, docSize), (node, pos) => {
					if (remaining > 0 && node.type.name === 'bodiedSyncBlock') {
						const endOfNode = pos + node.nodeSize;
						if (endOfNode > newToPos) {
							newToPos = Math.min(endOfNode, userContentEndPos);
						}
						remaining--;
					}
					return false;
				});
				adjusted = newToPos !== toPos;
			}
		}
	} catch {
		return bailout;
	}

	return { startPos, toPos: newToPos, adjusted };
}

// ============================================================================
// Slice OpenStart/OpenEnd Adjustment
// ============================================================================

/**
 * Adjusts slice openStart/openEnd to prevent unwanted node merging.
 *
 * This mirrors the logic from adjustSliceOpenStartEnd in useInEditorDirectStreaming.ts:
 * - If the first node in fragment is different from the existing first node at startPos,
 *   adjust openStart to 0 to avoid merging (unless both are table nodes)
 * - If the last node in fragment is different from the existing last node at toPos,
 *   adjust openEnd to 0 to avoid merging (unless both are table nodes)
 */
export function calculateSliceOpenStartEndAdjustment({
	state,
	slice,
	startPos,
	toPos,
}: {
	slice: Slice;
	startPos: number;
	state: EditorState;
	toPos: number;
}): Slice {
	const doc = state.doc;
	const schema = state.schema;
	let fragment = slice.content;
	const docSize = doc.content.size;

	// CCI-16810: ADFStreamer feeds the streamer a "doc" containing the
	// AI-supplied replacement content. When the supplied content is a child of
	// a wrapper list (e.g. taskItem or decisionItem) but the doc root requires
	// the wrapper (taskList / decisionList) for schema validity, createAndFill
	// wraps the content automatically. If the replacement target already lives
	// inside that wrapper, the wrapper becomes a redundant level — ProseMirror
	// then happily inserts it as a (schema-valid) nested list, producing
	// e.g. `taskList(taskList(taskItem))`.
	//
	// Unwrap the redundant outer wrapper when:
	//   - the fragment's first child is the wrapper type
	//   - the resolved position already sits inside a node of the same type
	//   - that wrapper's children would all be valid children of the existing
	//     parent (we keep this conservative — only the (taskList/taskItem) and
	//     (decisionList/decisionItem) pairs that triggered CCI-16810).
	if (
		fragment.firstChild &&
		fragment.childCount === 1 &&
		startPos >= 0 &&
		startPos <= docSize &&
		fg('platform_editor_ai_task_item_slice_unwrap_fix')
	) {
		const firstNode = fragment.firstChild;
		const wrapperPairs: ReadonlyArray<readonly [NodeType | undefined, NodeType | undefined]> = [
			[schema.nodes.taskList, schema.nodes.taskItem],
			[schema.nodes.decisionList, schema.nodes.decisionItem],
		];
		const matchedPair = wrapperPairs.find(
			([wrapper, item]) => !!wrapper && !!item && firstNode.type === wrapper,
		);
		if (matchedPair) {
			const [, itemType] = matchedPair;
			let allChildrenAreItems = firstNode.childCount > 0;
			firstNode.content.forEach((child) => {
				if (child.type !== itemType) {
					allChildrenAreItems = false;
				}
			});
			if (allChildrenAreItems) {
				// startPos is already bounds-checked above, so doc.resolve cannot throw.
				const $sp = doc.resolve(startPos);
				let parentIsSameWrapper = false;
				for (let depth = $sp.depth; depth >= 0; depth--) {
					if ($sp.node(depth).type === firstNode.type) {
						parentIsSameWrapper = true;
						break;
					}
				}
				if (parentIsSameWrapper) {
					// Strip the outer wrapper so the child item(s) merge with
					// the existing list instead of nesting under a new one.
					// We decrement openStart/openEnd by exactly one — the depth
					// we're removing — rather than re-deriving them via
					// Slice.maxOpen(fragment). maxOpen would over-open in the
					// `openStart === 0` case (closed wrapper, self-contained
					// list) and could cause unintended inline merging at the
					// list boundary.
					fragment = firstNode.content;
					slice = new SliceClass(
						fragment,
						Math.max(0, slice.openStart - 1),
						Math.max(0, slice.openEnd - 1),
					);
				}
			}
		}
	}

	// If the first node in fragment is different from the existing first node,
	// we need to adjust the slice open start to 0,
	// to avoid merging the first node in fragment with the existing first node,
	// for example, if the first node in fragment is a panel node, we'll lose the panel wrapper
	// (only the panel content is inserted into the doc)
	// but we don't need to do this if both nodes are table nodes
	const firstNode = fragment.firstChild;
	if (firstNode && startPos >= 0 && startPos <= docSize) {
		try {
			// bodiedSyncBlock must never be merged — its attrs (localId, resourceId)
			// must come from the streamed response, not the existing doc node.
			if (firstNode.type.name === 'bodiedSyncBlock') {
				slice = new SliceClass(fragment, 0, slice.openEnd);
			} else {
				const firstNodeIsTable = isTableStructureNode(firstNode.type, schema);
				const $startPos = doc.resolve(startPos);
				const startPosInTable = getNodeTypeDepth($startPos, schema.nodes.table) !== null;
				const bothTableNodes = firstNodeIsTable && startPosInTable;

				if (!bothTableNodes) {
					const existingFirstNode = $startPos.depth >= 1 ? $startPos.node(1) : null;
					if (!existingFirstNode || firstNode.type !== existingFirstNode.type) {
						slice = new SliceClass(fragment, 0, slice.openEnd);
					}
				}
			}
			// eslint-disable-next-line no-unused-vars
		} catch (e) {
			// On error, don't adjust
		}
	}

	// If the last node in fragment is different from the existing last node,
	// we need to adjust the slice open end to 0,
	// to avoid merging the last node in fragment with the existing last node,
	// for example, we've seen during streaming, heading and paragraph are merged together
	// (leading to a very long heading containing the paragraph content)
	// but we don't need to do this if both nodes are table nodes
	const lastNode = fragment.lastChild;
	if (lastNode && toPos >= 0 && toPos <= docSize) {
		try {
			// bodiedSyncBlock must never be merged — same reason as above.
			if (lastNode.type.name === 'bodiedSyncBlock') {
				slice = new SliceClass(fragment, slice.openStart, 0);
			} else {
				const lastNodeIsTable = isTableStructureNode(lastNode.type, schema);
				const $toPos = doc.resolve(toPos);
				const toPosInTable = getNodeTypeDepth($toPos, schema.nodes.table) !== null;
				const bothTableNodes = lastNodeIsTable && toPosInTable;

				if (!bothTableNodes) {
					const existingLastNode = $toPos.depth >= 1 ? $toPos.node(1) : null;
					if (!existingLastNode || lastNode.type !== existingLastNode.type) {
						slice = new SliceClass(fragment, slice.openStart, 0);
					}
				}
			}
			// eslint-disable-next-line no-unused-vars
		} catch (e) {
			// On error, don't adjust
		}
	}

	return slice;
}

function isTableStructureNode(nodeType: NodeType, schema: Schema): boolean {
	const { table, tableRow, tableHeader, tableCell } = schema.nodes;
	return [table, tableRow, tableHeader, tableCell].includes(nodeType);
}

function getNodeTypeDepth(resolvedPos: ResolvedPos, nodeType: NodeType | undefined): number | null {
	if (!nodeType) {
		return null;
	}
	for (let depth = resolvedPos.depth; depth > 0; depth--) {
		if (resolvedPos.node(depth).type === nodeType) {
			return depth;
		}
	}
	return null;
}
